/**
 * redact.ts
 *
 * Deep-scrubs PII from any value before it leaves the browser.
 * Called in Sentry beforeSend/beforeBreadcrumb, logger payloads,
 * and analytics event props. Never pass guest identifiers, transcript
 * text, phone numbers, emails, or credentials through observability
 * without running this first.
 */

/** Pattern matching e-mail addresses. */
const EMAIL_RE = /[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}/g;

/**
 * Pattern matching E.164 and common phone number formats.
 * Matches: +359 88 123 4567, (123) 456-7890, 0888123456, etc.
 * Deliberately broad to avoid false negatives.
 */
const PHONE_RE = /(\+?\d{1,3}[\s.-]?)?(\(?\d{2,4}\)?[\s.-]?)(\d{2,4}[\s.-]?){2,4}\d{2,4}/g;

/** JWT / bearer token — three base64url segments separated by dots. */
const JWT_RE = /eyJ[A-Za-z0-9_-]{10,}\.eyJ[A-Za-z0-9_-]{10,}\.[A-Za-z0-9_-]{10,}/g;

/** Generic API key / secret heuristic — long hex or base64 strings. */
const API_KEY_RE = /\b([A-Za-z0-9+/]{32,}={0,2})\b/g;

const REDACTED = '[REDACTED]';

/**
 * Sensitive object-key names. When a key matches, the entire value is
 * replaced regardless of whether the value itself looks like PII.
 */
const SENSITIVE_KEYS = new Set([
  'password',
  'passwd',
  'secret',
  'token',
  'authorization',
  'auth',
  'apikey',
  'api_key',
  'accesstoken',
  'access_token',
  'refreshtoken',
  'refresh_token',
  'transcript',
  'transcriptText',
  'transcript_text',
  'phone',
  'phoneNumber',
  'phone_number',
  'email',
  'emailAddress',
  'email_address',
  'creditcard',
  'credit_card',
  'cardnumber',
  'card_number',
  'cvv',
  'pii',
  'guestname',
  'guest_name',
  'credential',
  'credentials',
  'private_key',
  'privatekey',
  'client_secret',
  'clientsecret',
  'webhooksecret',
  'webhook_secret',
  'dsn',
]);

/** Normalise an object key for case-insensitive / underscore-free comparison. */
function normaliseKey(key: string): string {
  return key.toLowerCase().replace(/[_\-\s]/g, '');
}

/** Scrub known PII patterns from a string value. */
function scrubString(value: string): string {
  return value
    .replace(EMAIL_RE, REDACTED)
    .replace(PHONE_RE, REDACTED)
    .replace(JWT_RE, REDACTED)
    .replace(API_KEY_RE, (match) => {
      // Only redact if it looks like an actual key (high entropy / unusual chars).
      // Skip short words that happen to be long alphanumeric (e.g. "production").
      if (match.length >= 40) return REDACTED;
      return match;
    });
}

/** Tracks visited object references to prevent circular recursion. */
type RedactOptions = {
  _visited?: WeakSet<object>;
  _depth?: number;
};

const MAX_DEPTH = 10;

/**
 * Deeply redacts PII from any serialisable value.
 * - Strings: patterns (email, phone, JWT, long API keys) are replaced.
 * - Objects: sensitive keys are blanked; values are recursed.
 * - Arrays: each element is recursed.
 * - Primitives (number, boolean, null, undefined): returned unchanged.
 *
 * @param value - Any value that may contain PII.
 * @returns A new value with all detectable PII replaced by `'[REDACTED]'`.
 */
export function redact(value: unknown, options: RedactOptions = {}): unknown {
  const visited = options._visited ?? new WeakSet<object>();
  const depth = options._depth ?? 0;

  if (depth > MAX_DEPTH) return REDACTED;

  if (value === null || value === undefined) return value;

  if (typeof value === 'string') return scrubString(value);

  if (typeof value === 'number' || typeof value === 'boolean') return value;

  if (Array.isArray(value)) {
    if (visited.has(value)) return '[Circular]';
    visited.add(value);
    return value.map((item) => redact(item, { _visited: visited, _depth: depth + 1 }));
  }

  if (typeof value === 'object') {
    if (visited.has(value as object)) return '[Circular]';
    visited.add(value as object);

    const result: Record<string, unknown> = {};
    for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
      if (SENSITIVE_KEYS.has(normaliseKey(key))) {
        result[key] = REDACTED;
      } else {
        result[key] = redact(val, { _visited: visited, _depth: depth + 1 });
      }
    }
    return result;
  }

  // Functions, symbols, etc. — strip them.
  return undefined;
}

/** Exported for use in allowlist/deny-list extension. */
export { REDACTED, SENSITIVE_KEYS };
